/**
 * Task #1400 — Personal Assistant Workspace contracts.
 *
 * Single source of truth condivisa tra backend (route handlers + extractor +
 * agent loop) e frontend (registry viewer/editor, hooks, SSE consumer).
 *
 * Regole:
 *  - mai `any`, mai cast non documentati
 *  - ogni `ArtifactKind` ha schema Zod dedicato sotto `ArtifactContentSchemas`
 *  - eventi SSE = union discriminata su `type`, additivi rispetto al protocollo
 *    legacy (`content`/`tool_call`/`tool_result`/`done`/`error`/...)
 */

import { z } from 'zod';
import type { PaJob, PaJobStepStatus } from './pa-jobs';

// ---------------------------------------------------------------------------
// Artifact kinds (17 totali, 5 implementati MVP — vedere `ArtifactRegistry`).
// ---------------------------------------------------------------------------
export const ARTIFACT_KINDS = [
  'markdown',
  'code',
  'chart',
  'table',
  'file',
  'image',
  'video',
  'audio',
  'html',
  'react',
  'form',
  'diagram',
  'map',
  'timeline',
  'kanban',
  'mindmap',
  'action',
] as const;

export type ArtifactKind = typeof ARTIFACT_KINDS[number];

export const ARTIFACT_KIND_LABELS: Record<ArtifactKind, string> = {
  markdown: 'Documento',
  code: 'Codice',
  chart: 'Grafico',
  table: 'Tabella',
  file: 'File',
  image: 'Immagine',
  video: 'Video',
  audio: 'Audio',
  html: 'Pagina HTML',
  react: 'Componente React',
  form: 'Form',
  diagram: 'Diagramma',
  map: 'Mappa',
  timeline: 'Timeline',
  kanban: 'Kanban',
  mindmap: 'Mind map',
  action: 'Azione proposta',
};

export const ArtifactKindSchema = z.enum(ARTIFACT_KINDS);

// ---------------------------------------------------------------------------
// Per-kind content schemas
// ---------------------------------------------------------------------------
const MarkdownContentSchema = z.object({
  body: z.string(),
});
export type MarkdownContent = z.infer<typeof MarkdownContentSchema>;

const CodeContentSchema = z.object({
  language: z.string().min(1),
  body: z.string(),
});
export type CodeContent = z.infer<typeof CodeContentSchema>;

const ChartContentSchema = z.object({
  type: z.enum(['pie', 'bar', 'line', 'area']),
  title: z.string().optional(),
  data: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))),
  xKey: z.string(),
  yKey: z.string(),
  colors: z.array(z.string()).optional(),
});
export type ChartContent = z.infer<typeof ChartContentSchema>;

const TableCellSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const TableContentSchema = z.object({
  columns: z.array(z.object({
    key: z.string(),
    label: z.string(),
    type: z.enum(['text', 'number', 'boolean', 'date']).default('text'),
  })),
  rows: z.array(z.record(z.string(), TableCellSchema)),
});
export type TableContent = z.infer<typeof TableContentSchema>;

const ActionContentSchema = z.object({
  intent: z.string(),
  description: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()).default({}),
  cta: z.string().default('Esegui'),
  destructive: z.boolean().default(false),
});
export type ActionContent = z.infer<typeof ActionContentSchema>;

// Stub schemas — registry-ready ma viewer/editor sono follow-up.
const FileContentSchema = z.object({
  url: z.string().url().optional(),
  name: z.string(),
  mimeType: z.string(),
  size: z.number().int().nonnegative().optional(),
  preview: z.string().optional(),
});
const ImageContentSchema = z.object({
  url: z.string(),
  alt: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});
const VideoContentSchema = z.object({
  url: z.string(),
  poster: z.string().optional(),
  durationSec: z.number().nonnegative().optional(),
});
const AudioContentSchema = z.object({
  url: z.string(),
  durationSec: z.number().nonnegative().optional(),
  transcript: z.string().optional(),
});
const HtmlContentSchema = z.object({
  body: z.string(),
  sandboxed: z.boolean().default(true),
});
const ReactContentSchema = z.object({
  body: z.string(),
  imports: z.array(z.string()).default([]),
});
const FormFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'email', 'select', 'textarea', 'checkbox', 'date']),
  required: z.boolean().default(false),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
});
const FormContentSchema = z.object({
  fields: z.array(FormFieldSchema),
  submitLabel: z.string().default('Invia'),
  toolName: z.string().optional(),
});
const DiagramContentSchema = z.object({
  syntax: z.enum(['mermaid']).default('mermaid'),
  body: z.string(),
});
const MapContentSchema = z.object({
  center: z.tuple([z.number(), z.number()]),
  zoom: z.number().int().min(1).max(20),
  markers: z.array(z.object({
    lat: z.number(),
    lng: z.number(),
    label: z.string().optional(),
  })).default([]),
});
const TimelineItemSchema = z.object({
  id: z.string(),
  date: z.string(),
  title: z.string(),
  description: z.string().optional(),
});
const TimelineContentSchema = z.object({
  items: z.array(TimelineItemSchema),
});
const KanbanCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
});
const KanbanContentSchema = z.object({
  columns: z.array(z.object({
    id: z.string(),
    title: z.string(),
    cards: z.array(KanbanCardSchema),
  })),
});
const MindmapNodeSchema: z.ZodType<{ id: string; label: string; children?: Array<{ id: string; label: string; children?: unknown }> }> = z.lazy(() =>
  z.object({
    id: z.string(),
    label: z.string(),
    children: z.array(MindmapNodeSchema).optional(),
  }),
);
const MindmapContentSchema = z.object({
  root: MindmapNodeSchema,
});

/**
 * Lookup parametrico Kind → Schema. Usato dall'extractor + dalle route per
 * validazione con messaggi di errore coerenti.
 */
export const ArtifactContentSchemas = {
  markdown: MarkdownContentSchema,
  code: CodeContentSchema,
  chart: ChartContentSchema,
  table: TableContentSchema,
  action: ActionContentSchema,
  file: FileContentSchema,
  image: ImageContentSchema,
  video: VideoContentSchema,
  audio: AudioContentSchema,
  html: HtmlContentSchema,
  react: ReactContentSchema,
  form: FormContentSchema,
  diagram: DiagramContentSchema,
  map: MapContentSchema,
  timeline: TimelineContentSchema,
  kanban: KanbanContentSchema,
  mindmap: MindmapContentSchema,
} as const satisfies Record<ArtifactKind, z.ZodTypeAny>;

export type ArtifactContent =
  | { kind: 'markdown'; content: MarkdownContent }
  | { kind: 'code'; content: CodeContent }
  | { kind: 'chart'; content: ChartContent }
  | { kind: 'table'; content: TableContent }
  | { kind: 'action'; content: ActionContent }
  | { kind: 'file'; content: z.infer<typeof FileContentSchema> }
  | { kind: 'image'; content: z.infer<typeof ImageContentSchema> }
  | { kind: 'video'; content: z.infer<typeof VideoContentSchema> }
  | { kind: 'audio'; content: z.infer<typeof AudioContentSchema> }
  | { kind: 'html'; content: z.infer<typeof HtmlContentSchema> }
  | { kind: 'react'; content: z.infer<typeof ReactContentSchema> }
  | { kind: 'form'; content: z.infer<typeof FormContentSchema> }
  | { kind: 'diagram'; content: z.infer<typeof DiagramContentSchema> }
  | { kind: 'map'; content: z.infer<typeof MapContentSchema> }
  | { kind: 'timeline'; content: z.infer<typeof TimelineContentSchema> }
  | { kind: 'kanban'; content: z.infer<typeof KanbanContentSchema> }
  | { kind: 'mindmap'; content: z.infer<typeof MindmapContentSchema> };

// ---------------------------------------------------------------------------
// Persisted artifact (DB row → API response)
// ---------------------------------------------------------------------------
export const ArtifactStatusSchema = z.enum(['draft', 'published', 'edited', 'archived']);
export type ArtifactStatus = z.infer<typeof ArtifactStatusSchema>;

export const ArtifactSourceSchema = z.enum(['agent', 'user', 'tool']);
export type ArtifactSource = z.infer<typeof ArtifactSourceSchema>;

export interface PaArtifact {
  id: string;
  conversationId: string | null;
  messageId: string | null;
  /** Task #3822 — progetto PA di appartenenza (NULL = artefatto fuori progetto). */
  projectId: string | null;
  slug: string;
  version: number;
  kind: ArtifactKind;
  title: string;
  summary: string | null;
  content: unknown;
  metadata: Record<string, unknown>;
  status: ArtifactStatus;
  source: ArtifactSource;
  roundNumber: number | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Conversation workspace state (interrupt/resume con lock ottimistico)
// ---------------------------------------------------------------------------
export const WorkspaceStateSchema = z.enum([
  'idle',
  'running',
  'paused',
  'awaiting_approval',
  'completed',
]);
export type WorkspaceState = z.infer<typeof WorkspaceStateSchema>;

export interface PaWorkspaceStateSnapshot {
  conversationId: string;
  state: WorkspaceState;
  version: number;
  changedAt: string;
  reason: string | null;
}

// ---------------------------------------------------------------------------
// Tool approval contract
// ---------------------------------------------------------------------------
export const ApprovalDecisionSchema = z.enum(['pending', 'approved', 'rejected', 'expired']);
export type ApprovalDecision = z.infer<typeof ApprovalDecisionSchema>;

export interface PaToolApproval {
  id: string;
  conversationId: string;
  toolCallId: string;
  toolName: string;
  toolArgs: Record<string, unknown>;
  roundNumber: number;
  decision: ApprovalDecision;
  decidedAt: string | null;
  decisionNote: string | null;
  overrideArgs: Record<string, unknown> | null;
  expiresAt: string;
  createdAt: string;
}

export const ApprovalDecideRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  note: z.string().max(2000).optional(),
  overrideArgs: z.record(z.string(), z.unknown()).optional(),
});
export type ApprovalDecideRequest = z.infer<typeof ApprovalDecideRequestSchema>;

// ---------------------------------------------------------------------------
// SSE event union — discriminated on `type`, additive over legacy events.
// Legacy events (kept for back-compat): conversation_id, content, tool_call,
// tool_result, status, model_fallback, server_tool_use, citation,
// tools_summary, native_tools_summary, citations_summary, done, error.
// ---------------------------------------------------------------------------
export type PaStreamEvent =
  // legacy
  | { type: 'conversation_id'; conversation_id: string }
  | { type: 'content'; content: string }
  | { type: 'tool_call'; tool: string; status?: string }
  | { type: 'tool_result'; tool: string; status: 'success' | 'error' }
  | { type: 'status'; status: string; message?: string }
  | { type: 'model_fallback'; original_model: string | null; fallback_model: string; message: string }
  | { type: 'server_tool_use'; tool: string; id: string }
  | { type: 'citation'; url: string; title?: string; citedText?: string }
  | { type: 'tools_summary'; tools: Array<{ name: string; status: string }> }
  | {
      type: 'native_tools_summary';
      counts: { web_search: number; code_interpreter: number };
      items: Array<{ id: string; name: string; input?: unknown }>;
    }
  | { type: 'citations_summary'; citations: Array<{ url: string; title?: string; citedText?: string }> }
  | { type: 'done'; conversation_id: string }
  | { type: 'error'; error: string }
  // Task #2327 — ragionamento inline (stile Claude): chunk di "thinking"
  // accumulati per la risposta assistant corrente. Emesso solo quando il
  // provider/modello produce reasoning; altrimenti silenzioso.
  | { type: 'thinking'; content: string }
  // task #1400 additive
  | { type: 'thinking_start'; round: number }
  | { type: 'thinking_end'; round: number }
  | { type: 'tool_start'; tool: string; tool_call_id: string; round: number; args_preview: string }
  | { type: 'tool_end'; tool: string; tool_call_id: string; round: number; status: 'success' | 'error'; duration_ms: number }
  | { type: 'artifact_emitted'; artifact: PaArtifact }
  | { type: 'tool_approval_required'; approval: PaToolApproval }
  | { type: 'tool_approval_resolved'; approval_id: string; decision: ApprovalDecision }
  | { type: 'state_changed'; state: WorkspaceState; version: number; reason?: string }
  | { type: 'interrupted'; reason: string }
  | { type: 'resumed'; round: number }
  // Task #1445 — auto-title sync emesso prima di [DONE] nello stesso stream
  | { type: 'conversation_titled'; conversation_id: string; title: string }
  // Task #3821 — skill PA attivata nel turno: 'explicit' = /skill o stato
  // conversazione; 'auto' = aggancio autonomo per pertinenza (MAI invisibile:
  // l'evento è la dichiarazione strutturata, il modello la ribadisce in chat).
  | { type: 'skill_activated'; skill_id: string; skill_name: string; mode: 'explicit' | 'auto' }
  /** Richiamo esplicito `/skill` non onorato in pieno: mai scartato in silenzio. */
  | { type: 'skill_limit'; skill_id: string; skill_name: string | null; reason: 'not_available' | 'over_cap' | 'truncated' }
  // Task #3820 — Lavori PA: piano proposto (plan mode), aggiornamenti di
  // stato del lavoro e avanzamento per-passo. Additivi: i turni senza
  // trigger conservativo non emettono mai questi eventi.
  | { type: 'job_proposed'; job: PaJob }
  | { type: 'job_updated'; job: PaJob }
  | { type: 'job_step'; job_id: string; step_id: string; status: PaJobStepStatus };

export type PaStreamEventType = PaStreamEvent['type'];

// ---------------------------------------------------------------------------
// API request/response schemas
// ---------------------------------------------------------------------------
export const CreateArtifactRequestSchema = z.object({
  conversationId: z.string().uuid(),
  slug: z.string().min(1).max(120),
  kind: ArtifactKindSchema,
  title: z.string().min(1).max(200),
  summary: z.string().max(2000).optional(),
  content: z.unknown(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  roundNumber: z.number().int().nonnegative().optional(),
  source: ArtifactSourceSchema.default('user'),
});
export type CreateArtifactRequest = z.infer<typeof CreateArtifactRequestSchema>;

export const UpdateArtifactRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  summary: z.string().max(2000).optional(),
  content: z.unknown().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  status: ArtifactStatusSchema.optional(),
});
export type UpdateArtifactRequest = z.infer<typeof UpdateArtifactRequestSchema>;

export const InterruptRequestSchema = z.object({
  reason: z.string().max(500).optional(),
  expectedVersion: z.number().int().nonnegative(),
});
export type InterruptRequest = z.infer<typeof InterruptRequestSchema>;

export const ResumeRequestSchema = z.object({
  expectedVersion: z.number().int().nonnegative(),
  instruction: z.string().max(2000).optional(),
});
export type ResumeRequest = z.infer<typeof ResumeRequestSchema>;

export const ArtifactFormSubmissionValueSchema = z.union([z.string(), z.number(), z.boolean()]);
export const MemoryUpsertRequestSchema = z.object({
  memoryType: z.enum(['fact', 'preference', 'instruction', 'context']).default('fact'),
  memoryKey: z.string().min(1).max(255),
  memoryValue: z.string().min(1),
  confidence: z.number().min(0).max(1).optional(),
});
export type MemoryUpsertRequest = z.infer<typeof MemoryUpsertRequestSchema>;

export interface PaMemoryEntry {
  id: string;
  assistantId: string;
  memoryType: 'fact' | 'preference' | 'instruction' | 'context';
  memoryKey: string;
  memoryValue: string;
  confidence: number;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ArtifactFormSubmissionValue = z.infer<typeof ArtifactFormSubmissionValueSchema>;

export const ArtifactFormSubmissionSchema = z.object({
  artifactId: z.string().uuid(),
  slug: z.string().min(1).max(120),
  title: z.string().max(200).optional(),
  /** Tool suggerito dal form (se il modello lo ha indicato in `content.toolName`). */
  toolName: z.string().max(200).optional(),
  values: z.record(z.string(), ArtifactFormSubmissionValueSchema),
});

export type ArtifactFormSubmission = z.infer<typeof ArtifactFormSubmissionSchema>;
